import {
  CurrencyValue,
  formatDatetime,
  Chart,
  formatNumber
} from '@blockanalitica/ui';
import { ChartSkeleton } from '@/components';
import { DEFAULT_PALETTE } from '@/constants';
import { useMemo, useCallback, ReactNode } from 'react';
import {
  ChartConfig,
  ChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import { useChartPeriod } from '@/hooks';

export interface DatesData {
  [ilk: string]: { period: string; value: string }[];
}

export default function DatesLiquidationGraph({
  data: auctionData,
  timeFilter,
  daysAgo
}: {
  data: DatesData;
  timeFilter?: ReactNode;
  daysAgo: number;
}) {
  const chartDatasets: ChartDataset[] = useMemo(() => {
    return (
      auctionData &&
      Object.entries(auctionData).map(([key, rows], index) => ({
        label: key,
        data: rows.map((row) => ({
          x: row['period'],
          y: row['value']
        })),
        borderColor: DEFAULT_PALETTE[index],
        backgroundColor: DEFAULT_PALETTE[index]
      }))
    );
  }, [auctionData]);

  const cfg: ChartConfig = useMemo(() => {
    return {
      type: 'bar',
      options: {
        scales: {
          x: {
            stacked: true
          },
          y: {
            stacked: true
          }
        },
        elements: {
          bar: {
            hoverBorderWidth: 0
          }
        }
      }
    };
  }, []);

  const [periodInfo, updateChartConfigWithPeriod] = useChartPeriod(daysAgo);
  const config = updateChartConfigWithPeriod(cfg);

  if (Number(daysAgo) === 365) {
    //@ts-ignore
    config.options.scales.x.time.unit = 'months';
    periodInfo.dateFormat = 'MMM yyyy';
    periodInfo.labelPrefix = '';
  }

  if (Number(daysAgo) === 90) {
    periodInfo.labelPrefix = 'Week starting ';
  }

  const xScaleValueFormatter = useCallback(
    (value: number) => {
      return formatDatetime(value, {
        format: Number(daysAgo) >= 365 ? 'MMM yyyy' : 'MMM dd'
      });
    },
    [daysAgo]
  );

  const valueFormatter = useCallback(
    (value: string) => (
      <CurrencyValue
        value={value}
        options={{
          currency: 'USD'
        }}
      />
    ),
    []
  );

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {periodInfo.labelPrefix}
        {formatDatetime(value, { format: periodInfo.dateFormat })}
      </>
    ),
    [periodInfo]
  );

  if (auctionData) {
    return (
      <Chart
        chartDatasets={chartDatasets}
        config={config}
        timeFilter={timeFilter}
        valueFormatter={valueFormatter}
        subValueFormatter={subValueFormatter}
        xScaleValueFormatter={xScaleValueFormatter}
        tooltip={{
          labelFormatter: (value) => `${value}`,
          titleFormatter: (_value, tootlipItems) =>
            // @ts-ignore
            `${periodInfo.labelPrefix}${formatDatetime(tootlipItems[0].raw.x, { format: periodInfo.dateFormat })}`,
          valueFormatter: (value) => `$${formatNumber(Number(value))}`
        }}
        showKpisAsTooltip
      />
    );
  }
  return <ChartSkeleton />;
}
